import { z } from "zod";
import { ulidSchema } from "./schemas.js";
/**
 * A finished book, page by page, for reading on screen: the owner-approved edition, drawn from
 * the same files that print. Links are short-lived; the page asks again when they expire.
 */
const signedUrl = z
    .string()
    .url()
    .max(4_000)
    .refine((value) => value.startsWith("https://"));
export const editionPageV1Schema = z
    .object({
    url: signedUrl,
    /** "Cover", "Page 1"…: what a reader calls it. */
    label: z.string().min(1).max(40),
    /** The words printed on the page, for reading aloud or enlarging; empty when it has none. */
    text: z.string().max(20_000),
})
    .strict();
export const editionFamilySchema = z.enum(["BEDTIME", "VARIANT"]);
/**
 * A chapter or section of the book as a reader names it ("The Summer of 1962", "Grandma's
 * Biscuits"), for the reader's Contents (Stage 2, D8). `start` and `end` count the reader's pages,
 * where 0 is the cover: the section runs from `start` up to, not including, `end`.
 */
export const editionChapterV1Schema = z
    .object({
    id: z.string().min(1).max(160),
    title: z.string().min(1).max(160),
    start: z.number().int().min(1).max(400),
    end: z.number().int().min(2).max(401),
})
    .strict()
    .refine((chapter) => chapter.end > chapter.start, { message: "A chapter has pages." });
export const editionPagesResponseSchema = z.discriminatedUnion("state", [
    z
        .object({
        state: z.literal("READY"),
        bookId: ulidSchema,
        family: editionFamilySchema,
        /** The approval this edition is, so a page can tell when it changes. */
        editionKey: z.string().min(1).max(200),
        /** Page width over height, for laying out the reader before the pictures arrive. */
        aspect: z.number().positive().max(3),
        /** The front cover's own shape when known; otherwise the picture's own size tells. */
        coverAspect: z.number().positive().max(3).nullable(),
        /**
         * Whether each page's printed words came with it. False when the plan the edition was laid
         * out from has since been replaced: the pictures are the edition's, but its words aren't to
         * hand for finding or reading aloud.
         */
        textAvailable: z.boolean(),
        cover: editionPageV1Schema,
        pages: z.array(editionPageV1Schema).min(1).max(400),
        /**
         * The book's chapters or sections, in order, when its plan names them. Sent only to a reader
         * that asks for them (`responseVersion=2`): earlier readers check this object strictly.
         */
        chapters: z.array(editionChapterV1Schema).max(200).optional(),
        /**
         * A Bedtime edition's inside-page PDF these pictures were drawn from, so a print review can
         * offer them as a lighter look at that exact file (and never at a different one).
         */
        interiorChecksumSha256: z
            .string()
            .regex(/^[a-f0-9]{64}$/u)
            .optional(),
        expiresIn: z.number().int().positive().max(900),
    })
        .strict(),
    z
        .object({
        /** The pictures are being made from the edition's files; ask again shortly. */
        state: z.literal("PREPARING"),
        bookId: ulidSchema,
        family: editionFamilySchema,
        retryAfterSeconds: z.number().int().positive().max(60),
    })
        .strict(),
    z
        .object({
        /**
         * The pictures couldn't be made from the edition's files after several tries; the book is
         * finished and prints as approved, but can't be read here until someone looks.
         */
        state: z.literal("UNAVAILABLE"),
        bookId: ulidSchema,
        family: editionFamilySchema,
    })
        .strict(),
    z
        .object({
        /** No approved edition matches the book as it stands: it is still being made. */
        state: z.literal("NOT_FINISHED"),
        bookId: ulidSchema,
    })
        .strict(),
]);
/** One cover per finished book the reader can see, for a shelf. */
export const editionCoversResponseSchema = z
    .object({
    covers: z
        .array(z
        .object({
        bookId: ulidSchema,
        editionKey: z.string().min(1).max(200),
        /** Absent while a Bedtime edition's pictures are still being made. */
        url: signedUrl.nullable(),
        /** The front cover's width over height when known. */
        coverAspect: z.number().positive().max(3).nullable(),
    })
        .strict())
        .max(200),
    /** An authority read failed. These must not be labelled unfinished. */
    unavailableBookIds: z.array(ulidSchema).max(200).optional(),
    visibleBookIds: z.array(ulidSchema).max(200).optional(),
    expiresIn: z.number().int().positive().max(900),
})
    .strict();
/** Where a Bedtime edition's on-screen pictures are kept once made. */
export const bedtimeEditionPagesRecordV1Schema = z
    .object({
    version: z.literal(1),
    bookId: ulidSchema,
    /** The canonical render the approved edition review names. */
    proofJobId: ulidSchema,
    /** The interior PDF they were drawn from, so a different file is never shown as this one. */
    interiorChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    coverChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    aspect: z.number().positive().max(3),
    /** The front panel's width over height, as drawn. */
    coverAspect: z.number().positive().max(3),
    coverKey: z.string().min(1).max(1_024),
    pages: z
        .array(z
        .object({
        key: z.string().min(1).max(1_024),
        text: z.string().max(20_000),
    })
        .strict())
        .min(1)
        .max(64),
    createdAt: z.string().datetime(),
})
    .strict();
/** The worker's one job: draw a Bedtime edition's pages. */
export const bedtimeEditionPagesJobSchema = z
    .object({
    bookId: ulidSchema,
    proofJobId: ulidSchema,
})
    .strict();
//# sourceMappingURL=edition-pages.js.map